/**
 * CFleet Dispatcher App - Main Application Component
 *
 * This is the main entry point for the dispatcher (admin) dashboard.
 * It integrates authentication via @cfleet/auth with the full dispatcher UI
 * migrated from the prototype.
 *
 * Authentication:
 * - Uses Better Auth by default for all environments
 * - Set VITE_AUTH_PROVIDER=local only for unit tests with mocked auth
 * - VITE_BETTER_AUTH_URL should point to Better Auth sidecar (e.g., http://localhost:8443)
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  lazy,
  Suspense,
  Component,
  type ReactNode,
  type ErrorInfo,
} from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import {
  AuthProvider,
  ProtectedRoute,
  useAuth,
  useTenant,
  usePermissions,
  type AuthProviderType,
} from '@cfleet/auth';
import { WebSocketProvider } from '@cfleet/websocket';
import { useInboxMessages, useDispatchUnreadCount, useAddons, useToggleAddon } from '@cfleet/api-client';
import { useAuthConfig } from './hooks/useAuthConfig';

// Import lightweight components (static imports for auth pages and core UI)
import SignIn from './components/SignIn';
import SignUp from './components/SignUp';
import AcceptInvite from './components/AcceptInvite';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import Sidebar from './components/Sidebar';
import ResourceList from './components/ResourceList';
import ReconnectingBanner from './components/ReconnectingBanner';

// ErrorBoundary that catches chunk-load failures during lazy route imports.
// Without this, a failed dynamic import during a deploy mid-session crashes
// the entire dispatcher shell instead of showing a recoverable error.
interface ChunkErrorBoundaryState {
  hasError: boolean;
}
class ChunkErrorBoundary extends Component<{ children: ReactNode }, ChunkErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(_: Error): ChunkErrorBoundaryState {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Chunk load error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-slate-500">
          <p className="text-sm">This page failed to load. It may have been updated.</p>
          <button
            className="px-4 py-2 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
            onClick={() => window.location.reload()}
          >
            Reload to recover
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy load heavy dashboard components for code-splitting
const AIHelper = lazy(() => import('./components/AIHelper'));
const ImportData = lazy(() => import('./components/ImportData'));
const Integrations = lazy(() => import('./components/Integrations'));
const RoutePlanner = lazy(() => import('./components/RoutePlanner'));
const RouteSimulation = lazy(() => import('./components/RouteSimulation'));
const DispatchBoard = lazy(() => import('./components/DispatchBoard'));
const Analytics = lazy(() => import('./components/Analytics'));
const DriverList = lazy(() => import('./components/DriverList'));
const VehicleList = lazy(() => import('./components/VehicleList'));
const ClientManager = lazy(() => import('./components/ClientManager'));
const OrderManager = lazy(() => import('./components/OrderManager'));
const Inbox = lazy(() => import('./components/Inbox'));
const LiveTracking = lazy(() => import('./components/LiveTracking'));
const RecurringRoutes = lazy(() => import('./components/RecurringRoutes'));
const AddressBook = lazy(() => import('./components/AddressBook'));
const ChatHub = lazy(() => import('./components/chat/ChatHub'));
const Settings = lazy(() => import('./components/Settings'));
const RateCardManager = lazy(() => import('./components/RateCardManager'));
const RegionManager = lazy(() => import('./components/RegionManager'));
const AuditLog = lazy(() => import('./components/AuditLog'));

// Import types
import { FieldDefinition, Route as RouteType } from './types';

// Icons
import {
  Menu,
  AlertOctagon,
  FileText,
  Map as MapIcon,
  Wrench,
  MessageSquare,
  Globe,
  Shield,
  LogOut,
  ChevronRight,
  Settings as SettingsIcon,
  User as UserIcon,
  Activity,
  Bell,
  Building,
  ChevronDown,
  Check,
  Inbox as InboxIcon,
  LayoutDashboard,
  Package,
  Book,
  Radio,
  Users,
  Truck,
  BarChart2,
  FileUp,
  PlayCircle,
  Repeat,
  Kanban,
  Receipt,
  MapPinned,
  Calculator,
  Cable,
} from 'lucide-react';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8443/api/v1';
const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8443/ws';

// Auth provider configuration
// - 'better-auth': Cookie-based auth via Better Auth sidecar (default, for all environments)
// - 'local': Direct API calls with bearer tokens (for unit tests only)
const AUTH_PROVIDER = (import.meta.env.VITE_AUTH_PROVIDER || 'better-auth') as AuthProviderType;
const BETTER_AUTH_URL = import.meta.env.VITE_BETTER_AUTH_URL || '';

/**
 * Loading skeleton for lazy-loaded tab content.
 * Provides visual feedback while components are being loaded.
 */
const TabLoadingSkeleton: React.FC = () => (
  <div className="flex items-center justify-center h-full min-h-[400px]">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cfleet-600"></div>
      <p className="text-slate-400 text-sm">Loading...</p>
    </div>
  </div>
);

/**
 * Main Dispatcher Dashboard Component
 *
 * This contains the full dispatcher UI with sidebar navigation,
 * tab-based content rendering, and integration with shared state.
 */
/**
 * WebSocket wrapper component
 *
 * Provides WebSocket context for real-time updates.
 * Must be inside AuthProvider to access auth tokens.
 */
const WebSocketWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tokens } = useAuth();

  const getAuthToken = useCallback(() => tokens?.accessToken ?? null, [tokens]);

  const wsConfig = useMemo(() => ({ url: WS_URL, getAuthToken }), [getAuthToken]);

  return <WebSocketProvider config={wsConfig}>{children}</WebSocketProvider>;
};

const MobileNavItem = ({
  id,
  icon: Icon,
  label,
  activeTab,
  onSelect,
}: {
  id: string;
  icon: React.ElementType;
  label: string;
  activeTab: string;
  onSelect: (id: string) => void;
}) => (
  <button
    onClick={() => onSelect(id)}
    className={`w-full flex items-center px-2 py-1.5 rounded-md transition-colors mb-0.5
      ${activeTab === id ? 'bg-sidebar-hover text-white' : 'text-sidebar-text hover:bg-sidebar-hover hover:text-gray-200'}`}
  >
    <Icon size={14} className="mr-2.5" />
    <span className="text-sm font-medium">{label}</span>
  </button>
);

const DispatcherDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { currentOrganization, organizations, isMultiTenant, switchOrganization } = useTenant();
  const { isDispatcherOrAbove, hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('routes');
  const [activeSettingsSubTab, setActiveSettingsSubTab] = useState<
    'general' | 'access' | 'addons' | 'billing'
  >('general');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);
  const [isAIHelperOpen, setIsAIHelperOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const orgMenuRef = useRef<HTMLDivElement>(null);

  // --- Global State for Routing Operations ---
  const [activeRoutes, setActiveRoutes] = useState<RouteType[]>([]);
  const [plannerStatus, setPlannerStatus] = useState<'DRAFT' | 'OPTIMIZED' | 'DISPATCHED'>('DRAFT');

  // --- Global State for Settings/Add-ons ---
  // Addons are now server-sourced via React Query instead of local state
  const authConfig = useAuthConfig();
  const { data: addonsData, isLoading: addonsLoading } = useAddons(
    authConfig ? { authConfig } : undefined
  );
  const { mutate: toggleAddonMutation } = useToggleAddon(
    authConfig ? { authConfig } : undefined
  );

  // Extract installed addon IDs from the query response
  const installedAddons = addonsData?.addons
    ?.filter((addon) => addon.installed)
    .map((addon) => addon.id) ?? [];

  // Unread counts for sidebar badges
  const chatEnabled = installedAddons.includes('addon_chat');
  const unreadQuery = useDispatchUnreadCount(
    chatEnabled && authConfig ? { authConfig } : undefined
  );
  const { data: inboxData } = useInboxMessages(
    { limit: 1 },
    authConfig ? { authConfig } : undefined
  );
  const inboxUnreadCount = inboxData?.needs_action_count ?? inboxData?.unread_count ?? 0;

  // Click outside handler for user menu and org menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (orgMenuRef.current && !orgMenuRef.current.contains(event.target as Node)) {
        setIsOrgMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleAddon = (id: string) => {
    toggleAddonMutation({ addonId: id });
  };

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Get user display info
  const userInitials =
    user?.name?.substring(0, 2).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U';
  const userName = user?.name || 'User';
  const userEmail = user?.email || '';
  const userRole = user?.role || 'Dispatcher';

  // --- Configuration for Generic Resource Lists ---

  // Alerts (Read Only)
  const alertColumns = [
    {
      header: 'Alert Message',
      accessor: 'message',
      render: (val: string) => <span className="font-medium text-slate-800">{val}</span>,
    },
    {
      header: 'Severity',
      accessor: 'severity',
      render: (val: string) => (
        <span
          className={`uppercase text-xs font-bold ${val === 'Critical' ? 'text-rose-600' : 'text-amber-600'}`}
        >
          {val}
        </span>
      ),
    },
    {
      header: 'Time',
      accessor: 'time',
      render: (val: string) => <span className="text-slate-500 text-xs">{val}</span>,
    },
  ];
  const mockAlerts = [
    {
      id: '1',
      message: 'Vehicle V-202 Engine Overheat Warning',
      severity: 'Critical',
      time: '10 mins ago',
    },
    {
      id: '2',
      message: 'Driver Mike Ross late for Stop #4',
      severity: 'Warning',
      time: '25 mins ago',
    },
    { id: '3', message: 'GPS Signal lost for ID-441', severity: 'Warning', time: '1 hour ago' },
  ];

  // Invoices
  const invoiceColumns = [
    {
      header: 'Invoice #',
      accessor: 'id',
      render: (val: string) => <span className="font-mono text-cfleet-600">{val}</span>,
    },
    { header: 'Client', accessor: 'client' },
    { header: 'Amount', accessor: 'amount' },
    {
      header: 'Status',
      accessor: 'status',
      render: (val: string) => (
        <span
          className={`text-xs px-2 py-0.5 rounded ${val === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
        >
          {val}
        </span>
      ),
    },
    { header: 'Due Date', accessor: 'dueDate' },
  ];
  const invoiceFields: FieldDefinition[] = [
    { name: 'client', label: 'Client', type: 'text' },
    { name: 'amount', label: 'Amount', type: 'text' },
    { name: 'dueDate', label: 'Due Date', type: 'date' },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: ['Draft', 'Sent', 'Paid', 'Overdue'],
    },
  ];
  const mockInvoices = [
    {
      id: 'INV-2023-001',
      client: 'Fresh Market Inc',
      amount: '$450.00',
      status: 'Paid',
      dueDate: '2023-11-01',
    },
    {
      id: 'INV-2023-002',
      client: 'BuildIt Construction',
      amount: '$1,200.50',
      status: 'Overdue',
      dueDate: '2023-10-25',
    },
  ];

  const renderContent = () => {
    // Helper to wrap lazy components in Suspense with chunk-error recovery
    const withSuspense = (component: React.ReactNode) => (
      <ChunkErrorBoundary>
        <Suspense fallback={<TabLoadingSkeleton />}>{component}</Suspense>
      </ChunkErrorBoundary>
    );

    switch (activeTab) {
      case 'dashboard':
        return withSuspense(<Analytics onNavigate={(tab) => setActiveTab(tab)} />);
      case 'inbox':
        return withSuspense(<Inbox onNavigate={(tab) => setActiveTab(tab)} />);
      case 'import_data':
        return withSuspense(
          <ImportData
            onImportComplete={(type, data) => {
              console.log(`Imported ${data.length} ${type} records`);
              // TODO: Wire up to actual data stores when backend is connected
            }}
          />
        );
      case 'integrations':
        return withSuspense(
          <Integrations
            onIntegrationChange={(integrationId, connected) => {
              console.log(
                `Integration ${integrationId} ${connected ? 'connected' : 'disconnected'}`
              );
              // TODO: Wire up to actual integration management when backend is connected
            }}
          />
        );
      case 'settings':
        return withSuspense(
          <Settings
            installedAddons={installedAddons}
            onToggleAddon={handleToggleAddon}
            activeSubTab={activeSettingsSubTab}
            onSubTabChange={setActiveSettingsSubTab}
          />
        );

      // Planning Section
      case 'routes':
        return withSuspense(
          <RoutePlanner
            routes={activeRoutes.length > 0 ? activeRoutes : undefined}
            status={plannerStatus}
            onRoutesUpdate={setActiveRoutes}
            onStatusUpdate={setPlannerStatus}
          />
        );
      case 'address_book':
        return withSuspense(<AddressBook />);
      case 'simulation':
        return withSuspense(<RouteSimulation />);
      case 'recurring':
        return withSuspense(<RecurringRoutes />);

      // Dispatch Section
      case 'dispatch_board':
        return withSuspense(<DispatchBoard />);
      case 'tracking':
        return withSuspense(<LiveTracking />);
      case 'drivers':
        return withSuspense(<DriverList />);
      case 'vehicles':
        return withSuspense(<VehicleList onNavigate={(tab) => setActiveTab(tab)} />);
      case 'regions':
        return withSuspense(<RegionManager />);
      case 'alerts':
        return (
          <ResourceList
            title="System Alerts"
            description="Real-time notifications and critical issues."
            data={mockAlerts}
            columns={alertColumns}
            emptyIcon={AlertOctagon}
          />
        );

      // Dynamic Add-ons (Dispatch)
      case 'maintenance':
        return (
          <ResourceList
            title="Fleet Maintenance"
            description="Service records and repair schedules."
            data={[]}
            columns={[
              { header: 'Vehicle', accessor: 'vehicle' },
              { header: 'Issue', accessor: 'issue' },
            ]}
            emptyIcon={Wrench}
          />
        );
      case 'chat':
        return withSuspense(<ChatHub />);

      // Client Management Section
      case 'invoices':
        return (
          <ResourceList
            title="Invoices"
            description="Billing history and invoice generation."
            data={mockInvoices}
            columns={invoiceColumns}
            fields={invoiceFields}
            emptyIcon={FileText}
          />
        );
      case 'service_areas':
        return (
          <ResourceList
            title="Service Areas"
            description="Geographic zones for delivery coverage."
            data={[]}
            columns={[{ header: 'Zone Name', accessor: 'name' }]}
            fields={[{ name: 'name', label: 'Name', type: 'text' }]}
            emptyIcon={MapIcon}
          />
        );
      case 'rate_tables':
        return withSuspense(<RateCardManager />);
      case 'clients':
        return withSuspense(<ClientManager />);

      // Dynamic Add-ons (Client)
      case 'portal':
        return (
          <ResourceList
            title="Customer Portal"
            description="Configure client-facing access and branding."
            data={[]}
            columns={[{ header: 'Setting', accessor: 'key' }]}
            emptyIcon={Globe}
          />
        );

      // Business Section
      case 'orders':
        return withSuspense(<OrderManager />);
      case 'analytics':
        return withSuspense(<Analytics onNavigate={(tab) => setActiveTab(tab)} />);
      case 'audit':
        return withSuspense(<AuditLog />);

      default:
        return withSuspense(
          <RoutePlanner
            routes={activeRoutes.length > 0 ? activeRoutes : undefined}
            status={plannerStatus}
            onRoutesUpdate={setActiveRoutes}
            onStatusUpdate={setPlannerStatus}
          />
        );
    }
  };

  return (
    <div className="flex h-screen bg-white text-slate-900 font-sans overflow-hidden selection:bg-cfleet-200">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        enabledAddons={installedAddons}
        chatUnreadCount={chatEnabled ? unreadQuery.data?.unread_count || undefined : undefined}
        inboxUnreadCount={inboxUnreadCount}
        onOpenAIHelper={() => setIsAIHelperOpen(true)}
        onOpenImportData={() => setActiveTab('import_data')}
        onOpenIntegrations={() => setActiveTab('integrations')}
        onToggleAddon={(addonId) => toggleAddonMutation({ addonId })}
      />

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className="w-64 h-full bg-sidebar-bg text-sidebar-text border-r border-sidebar-border flex flex-col overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-4">
              <h2 className="text-xl font-bold text-white">cfleet</h2>
            </div>

            <nav className="flex-1 px-3 pb-3">
              {/* Overview */}
              <MobileNavItem
                id="inbox"
                icon={InboxIcon}
                label="Inbox"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="dashboard"
                icon={LayoutDashboard}
                label="Dashboard"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />

              {/* Planning */}
              <div className="mt-4 mb-1 px-2 text-xs font-semibold tracking-wide uppercase opacity-70">
                Planning
              </div>
              <MobileNavItem
                id="routes"
                icon={MapIcon}
                label="Routes Map"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="address_book"
                icon={Book}
                label="Address Book"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="simulation"
                icon={PlayCircle}
                label="Route Simulation"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="recurring"
                icon={Repeat}
                label="Recurring"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />

              {/* Dispatch & Fleet */}
              <div className="mt-4 mb-1 px-2 text-xs font-semibold tracking-wide uppercase opacity-70">
                Dispatch & Fleet
              </div>
              <MobileNavItem
                id="dispatch_board"
                icon={Kanban}
                label="Dispatch Board"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="tracking"
                icon={Radio}
                label="Live Tracking"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="drivers"
                icon={Users}
                label="Drivers & Team"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="vehicles"
                icon={Truck}
                label="Vehicles"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="alerts"
                icon={Bell}
                label="Real-time Alerts"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              {installedAddons.includes('addon_maintenance') && (
                <MobileNavItem
                  id="maintenance"
                  icon={Wrench}
                  label="Maintenance"
                  activeTab={activeTab}
                  onSelect={(id) => {
                    setActiveTab(id);
                    setIsMobileMenuOpen(false);
                  }}
                />
              )}
              {installedAddons.includes('addon_chat') && (
                <MobileNavItem
                  id="chat"
                  icon={MessageSquare}
                  label="Team Chat"
                  activeTab={activeTab}
                  onSelect={(id) => {
                    setActiveTab(id);
                    setIsMobileMenuOpen(false);
                  }}
                />
              )}

              {/* Client Management */}
              <div className="mt-4 mb-1 px-2 text-xs font-semibold tracking-wide uppercase opacity-70">
                Client Management
              </div>
              <MobileNavItem
                id="invoices"
                icon={Receipt}
                label="Invoices"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="service_areas"
                icon={MapPinned}
                label="Service Areas"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="rate_tables"
                icon={Calculator}
                label="Rate Tables"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="clients"
                icon={Building}
                label="Clients"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              {installedAddons.includes('addon_portal') && (
                <MobileNavItem
                  id="portal"
                  icon={Globe}
                  label="Portal Config"
                  activeTab={activeTab}
                  onSelect={(id) => {
                    setActiveTab(id);
                    setIsMobileMenuOpen(false);
                  }}
                />
              )}

              {/* Business */}
              <div className="mt-4 mb-1 px-2 text-xs font-semibold tracking-wide uppercase opacity-70">
                Business
              </div>
              <MobileNavItem
                id="orders"
                icon={Package}
                label="Orders"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="analytics"
                icon={BarChart2}
                label="Analytics"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              {hasPermission('viewAudit') && (
                <MobileNavItem
                  id="audit"
                  icon={FileText}
                  label="Audit Logs"
                  activeTab={activeTab}
                  onSelect={(id) => {
                    setActiveTab(id);
                    setIsMobileMenuOpen(false);
                  }}
                />
              )}
            </nav>

            {/* Bottom Section */}
            <div className="p-3 border-t border-sidebar-border mt-auto">
              <MobileNavItem
                id="import_data"
                icon={FileUp}
                label="Import Data"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="integrations"
                icon={Cable}
                label="Integrations"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
              <MobileNavItem
                id="settings"
                icon={SettingsIcon}
                label="Settings"
                activeTab={activeTab}
                onSelect={(id) => {
                  setActiveTab(id);
                  setIsMobileMenuOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 flex flex-col h-full overflow-hidden relative bg-slate-50/50">
        {/* Top Header */}
        <header className="h-12 bg-white border-b border-slate-200 flex items-center px-4 justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-slate-500 hover:text-slate-800"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className="font-medium text-slate-800">cfleet</span>
              <span className="text-slate-300">/</span>
              <span className="text-slate-600 capitalize">{activeTab.replace(/_/g, ' ')}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center text-xs text-slate-400 gap-1 bg-slate-50 px-2 py-1.5 rounded border border-slate-200 hover:border-slate-300 transition-colors cursor-text w-32 justify-between">
              <span>Search</span>
              <kbd className="font-sans text-[10px] bg-slate-200 rounded px-1">⌘K</kbd>
            </div>

            {/* Organization Switcher - Only shown for multi-tenant users */}
            {isMultiTenant && (
              <div className="relative" ref={orgMenuRef}>
                <button
                  onClick={() => setIsOrgMenuOpen(!isOrgMenuOpen)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border transition-all
                    ${
                      isOrgMenuOpen
                        ? 'bg-cfleet-50 border-cfleet-200 text-cfleet-700'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300'
                    }
                  `}
                >
                  <Building
                    size={14}
                    className={isOrgMenuOpen ? 'text-cfleet-500' : 'text-slate-400'}
                  />
                  <span className="max-w-[120px] truncate font-medium">
                    {currentOrganization?.name || 'Select Organization'}
                  </span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform ${isOrgMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Organization Dropdown Menu */}
                {isOrgMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-100 py-2 animate-in fade-in zoom-in-95 duration-200 origin-top-right z-50">
                    <div className="px-3 py-2 border-b border-slate-100">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        Switch Organization
                      </p>
                    </div>
                    <div className="py-1 max-h-64 overflow-y-auto">
                      {organizations.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => {
                            queryClient.clear();
                            switchOrganization(org.id);
                            setIsOrgMenuOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors
                            ${
                              currentOrganization?.id === org.id
                                ? 'bg-cfleet-50 text-cfleet-700'
                                : 'text-slate-600 hover:bg-slate-50'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold
                              ${
                                currentOrganization?.id === org.id
                                  ? 'bg-cfleet-100 text-cfleet-600'
                                  : 'bg-slate-100 text-slate-500'
                              }
                            `}
                            >
                              {org.name.substring(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium truncate">{org.name}</div>
                            </div>
                          </div>
                          {currentOrganization?.id === org.id && (
                            <Check size={16} className="text-cfleet-500 shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <span className="text-xs text-slate-500 hidden sm:inline">{userName}</span>

            {/* User Profile Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                data-testid="user-menu-button"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className={`w-7 h-7 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-[10px] ring-2 transition-all shadow-sm
                  ${isUserMenuOpen ? 'ring-cfleet-200 scale-105' : 'ring-white hover:ring-cfleet-100 hover:scale-105'}
                `}
              >
                {userInitials}
              </button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-100 py-2 animate-in fade-in zoom-in-95 duration-200 origin-top-right z-50">
                  {/* User Info Section */}
                  <div className="px-4 py-3 border-b border-slate-50">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm">
                        {userInitials}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-slate-900 truncate">{userName}</div>
                        <div className="text-[10px] text-slate-500 truncate">{userEmail}</div>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cfleet-50 border border-cfleet-100 text-cfleet-700 text-[10px] font-bold uppercase tracking-wider">
                      <Shield size={10} /> {userRole}
                    </div>
                  </div>

                  {/* Actions Group 1 */}
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setActiveTab('settings');
                        setActiveSettingsSubTab('general');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <UserIcon size={16} className="text-slate-400" />
                        <span>Personal Profile</span>
                      </div>
                      <ChevronRight size={14} className="text-slate-300" />
                    </button>

                    {userRole === 'Admin' && (
                      <button
                        onClick={() => {
                          setActiveTab('settings');
                          setActiveSettingsSubTab('access');
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <SettingsIcon size={16} className="text-slate-400" />
                          <span>Workspace Settings</span>
                        </div>
                        <ChevronRight size={14} className="text-slate-300" />
                      </button>
                    )}

                    {userRole === 'Dispatcher' && (
                      <button
                        onClick={() => {
                          setActiveTab('dashboard');
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Activity size={16} className="text-slate-400" />
                          <span>Fleet Health</span>
                        </div>
                        <ChevronRight size={14} className="text-slate-300" />
                      </button>
                    )}
                  </div>

                  <div className="border-t border-slate-50 my-1"></div>

                  {/* Bottom Actions */}
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setActiveTab('inbox');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center justify-between px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Bell size={16} className="text-slate-400" />
                        <span>Notifications</span>
                      </div>
                      <span className="bg-cfleet-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        2
                      </span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 transition-colors font-medium mt-1"
                    >
                      <LogOut size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden relative">{renderContent()}</div>
      </main>

      {/* AI Helper Overlay */}
      <Suspense fallback={null}>
        <AIHelper isOpen={isAIHelperOpen} onClose={() => setIsAIHelperOpen(false)} />
      </Suspense>
    </div>
  );
};

/**
 * Login Page Component
 *
 * Uses the migrated SignIn component from the prototype.
 */
const LoginPage: React.FC = () => {
  return <SignIn />;
};

/**
 * Main App Component
 *
 * Sets up providers and renders the application.
 * - BrowserRouter for routing
 * - QueryClientProvider for data fetching
 * - AuthProvider for authentication
 * - ProtectedRoute for route protection
 */
function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider
          apiBaseUrl={API_BASE_URL}
          appType="dispatcher"
          authProvider={AUTH_PROVIDER}
          betterAuthUrl={BETTER_AUTH_URL || undefined}
        >
          <ReconnectingBanner />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute loginPath="/login">
                  <WebSocketWrapper>
                    <DispatcherDashboard />
                  </WebSocketWrapper>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
